import { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Save, Mail, UserPlus, Building2, Eye, EyeOff, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { getApiUrl } from '@/lib/api';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Recipient {
  id: string;
  name: string;
  email: string;
  team?: string;
}

interface RegionalConfig {
  regional: string;
  recipients: Recipient[];
}

interface BccConfig {
  recipients: Recipient[];
}

interface EmailSettings {
  productionMode: boolean; // true = envia para todos, false = apenas para teste
  testEmail: string;
}

// Lista de regionais conhecidas
const REGIONALS = ['SP1', 'SP2', 'RJ/ES', 'MG', 'PR/SC', 'RS', 'BA/SE', 'NE', 'CO', 'NO'];

export function AdminRegionalsPage() {
  const [selectedRegional, setSelectedRegional] = useState<string | null>(null);
  const [regionalsConfig, setRegionalsConfig] = useState<Record<string, RegionalConfig>>({});
  const [bccConfig, setBccConfig] = useState<BccConfig>({ recipients: [] });
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    productionMode: false, // Default: modo teste
    testEmail: 'leonardo.camelo@crasdata.com.br',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBccDialogOpen, setIsBccDialogOpen] = useState(false);
  
  // Form state para novo destinatário
  const [newRecipient, setNewRecipient] = useState<Omit<Recipient, 'id'>>({
    name: '',
    email: '',
    team: '',
  });

  const { toast } = useToast();

  // Carregar configurações ao montar
  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/claro/api/email/regionals-config'));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status === 'success') {
        setRegionalsConfig(data.regionals || {});
        setBccConfig(data.bcc || { recipients: [] });
        setEmailSettings(data.settings || { productionMode: false, testEmail: 'leonardo.camelo@crasdata.com.br' });
      } else {
        throw new Error(data.message || 'Erro ao carregar configurações');
      }
    } catch (error) {
      toast({
        title: 'Erro ao carregar',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfigs = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(getApiUrl('/claro/api/email/regionals-config'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          regionals: regionalsConfig,
          bcc: bccConfig,
          settings: emailSettings,
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        toast({
          title: 'Configurações salvas',
          description: 'As configurações de destinatários foram atualizadas.',
        });
      } else {
        throw new Error(data.message || 'Erro ao salvar');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao salvar configurações',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenRegional = (regional: string) => {
    setSelectedRegional(regional);
    if (!regionalsConfig[regional]) {
      setRegionalsConfig(prev => ({
        ...prev,
        [regional]: { regional, recipients: [] },
      }));
    }
    setIsDialogOpen(true);
  };

  const handleAddRecipient = (isBcc = false) => {
    if (!newRecipient.name || !newRecipient.email) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Nome e email são obrigatórios.',
        variant: 'destructive',
      });
      return;
    }

    const recipient: Recipient = {
      id: crypto.randomUUID(),
      name: newRecipient.name,
      email: newRecipient.email,
      team: newRecipient.team || undefined,
    };

    if (isBcc) {
      setBccConfig(prev => ({
        ...prev,
        recipients: [...prev.recipients, recipient],
      }));
    } else if (selectedRegional) {
      setRegionalsConfig(prev => ({
        ...prev,
        [selectedRegional]: {
          ...prev[selectedRegional],
          recipients: [...(prev[selectedRegional]?.recipients || []), recipient],
        },
      }));
    }

    setNewRecipient({ name: '', email: '', team: '' });
  };

  const handleRemoveRecipient = (recipientId: string, isBcc = false) => {
    if (isBcc) {
      setBccConfig(prev => ({
        ...prev,
        recipients: prev.recipients.filter(r => r.id !== recipientId),
      }));
    } else if (selectedRegional) {
      setRegionalsConfig(prev => ({
        ...prev,
        [selectedRegional]: {
          ...prev[selectedRegional],
          recipients: prev[selectedRegional].recipients.filter(r => r.id !== recipientId),
        },
      }));
    }
  };

  const getRegionalRecipientCount = (regional: string) => {
    return regionalsConfig[regional]?.recipients?.length || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Configuração das Regionais</h2>
          <p className="text-muted-foreground">
            Cadastro de destinatários de emails por regional
          </p>
        </div>
        <Button onClick={saveConfigs} disabled={isSaving}>
          <Save className="mr-2 h-4 w-4" />
          {isSaving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>

      {/* Toggle Modo Produção/Teste */}
      <Card className={emailSettings.productionMode ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-amber-500 bg-amber-50 dark:bg-amber-950'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {emailSettings.productionMode ? (
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              )}
              <div>
                <h3 className="font-semibold text-lg">
                  {emailSettings.productionMode ? 'Modo Produção' : 'Modo Teste'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {emailSettings.productionMode 
                    ? 'Os emails serão enviados para todos os destinatários cadastrados em cada regional.'
                    : `Os emails serão enviados apenas para ${emailSettings.testEmail} (modo teste).`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="production-mode" className="text-sm font-medium">
                {emailSettings.productionMode ? 'Produção' : 'Teste'}
              </Label>
              <Switch
                id="production-mode"
                checked={emailSettings.productionMode}
                onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, productionMode: checked }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards das Regionais */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {REGIONALS.map((regional) => (
          <Card 
            key={regional} 
            className="cursor-pointer hover:border-primary transition-colors"
            onClick={() => handleOpenRegional(regional)}
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-lg">
                <span className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {regional}
                </span>
                <Badge variant="secondary">
                  {getRegionalRecipientCount(regional)} destinatários
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Clique para gerenciar os destinatários
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Card de Cópia Oculta */}
      <Card className="border-dashed border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <EyeOff className="h-5 w-5" />
            Cópia Oculta (BCC)
          </CardTitle>
          <CardDescription>
            Destinatários que receberão todos os emails em cópia oculta (não visível para as regionais)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {bccConfig.recipients.length} destinatários
              </Badge>
              {bccConfig.recipients.slice(0, 3).map((r) => (
                <Badge key={r.id} variant="secondary" className="text-xs">
                  {r.name}
                </Badge>
              ))}
              {bccConfig.recipients.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{bccConfig.recipients.length - 3} mais
                </Badge>
              )}
            </div>
            <Button variant="outline" onClick={() => setIsBccDialogOpen(true)}>
              <Users className="mr-2 h-4 w-4" />
              Gerenciar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog para destinatários da regional */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Destinatários - Regional {selectedRegional}
            </DialogTitle>
            <DialogDescription>
              Gerencie os destinatários de email para esta regional
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Formulário para adicionar */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Adicionar Destinatário
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[150px]">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={newRecipient.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Nome completo"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newRecipient.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <Label htmlFor="team">Time</Label>
                    <Input
                      id="team"
                      value={newRecipient.team}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, team: e.target.value }))
                      }
                      placeholder="(opcional)"
                    />
                  </div>
                  <Button onClick={() => handleAddRecipient(false)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tabela de destinatários */}
            <ScrollArea className="h-[300px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="w-[60px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedRegional && regionalsConfig[selectedRegional]?.recipients?.length > 0 ? (
                    regionalsConfig[selectedRegional].recipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell className="font-medium">{recipient.name}</TableCell>
                        <TableCell>{recipient.email}</TableCell>
                        <TableCell>{recipient.team || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveRecipient(recipient.id, false)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        Nenhum destinatário cadastrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para BCC */}
      <Dialog open={isBccDialogOpen} onOpenChange={setIsBccDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EyeOff className="h-5 w-5" />
              Cópia Oculta (BCC)
            </DialogTitle>
            <DialogDescription>
              Estes destinatários receberão todos os emails em cópia oculta, sem aparecer para as regionais
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Formulário para adicionar */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Adicionar Destinatário
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[150px]">
                    <Label htmlFor="bcc-name">Nome *</Label>
                    <Input
                      id="bcc-name"
                      value={newRecipient.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Nome completo"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="bcc-email">Email *</Label>
                    <Input
                      id="bcc-email"
                      type="email"
                      value={newRecipient.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <Label htmlFor="bcc-team">Time</Label>
                    <Input
                      id="bcc-team"
                      value={newRecipient.team}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setNewRecipient(prev => ({ ...prev, team: e.target.value }))
                      }
                      placeholder="(opcional)"
                    />
                  </div>
                  <Button onClick={() => handleAddRecipient(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tabela de destinatários BCC */}
            <ScrollArea className="h-[300px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="w-[60px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bccConfig.recipients.length > 0 ? (
                    bccConfig.recipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell className="font-medium">{recipient.name}</TableCell>
                        <TableCell>{recipient.email}</TableCell>
                        <TableCell>{recipient.team || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveRecipient(recipient.id, true)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        Nenhum destinatário cadastrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBccDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
